import axios, { AxiosRequestConfig, AxiosPromise, InternalAxiosRequestConfig } from 'axios';
import { AuthHelper } from './AuthHelper';
import { INewsListItem, INewsItem } from 'src/Entities/News/NewsItem';
import { IAdminNewsFormSubmitValues } from 'src/Pages/CoursePortal/AdminNews/components/NewsForm';
import { INewPage, IPage } from 'src/Entities/Pages';
import { ITheme } from 'src/Entities/Theme';
import { IThemeSectionFormValues } from 'src/Pages/CoursePortal/ThemeSectionEditor/components/ThemeSectionForm';
import {
  ICourseSearchResultItem,
  INewsSearchResultItem
} from 'src/types/SearchResult';
import { ISearchBarState } from 'src/components/SearchBar';
import { IDepartment } from 'src/Entities/Departments';
import { IGetStatisticResultOption, IQueueStatisticReportOptions, IStatisticResult, IStatisticsDepartmentItem } from 'src/types/Statistics';
import { EmailList } from 'src/types/EmailLists';
import { ICourse, ICoursesGroupedByCategory, IRawCourse, ITitleItem } from 'src/types/Courses';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST || '',
  timeout: 100000
});

// Apply user token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = AuthHelper.GetUserToken();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

export interface IFetchNewsResponse {
  newsList: INewsListItem[];
  page: number;
  totalPages: number;
}

export async function fetchNews(
  pageNumber: number = 1,
  pageSize: number = 10,
  isAdminView?: boolean
): Promise<IFetchNewsResponse> {
  const options = {
    PageSize: pageSize,
    Pagenumber: pageNumber,
    IsAdminView: isAdminView
  };

  const { data } = await api.post('/news/GetNews', options);

  return {
    ...data,
    newsList: data.newsList.map((item: any) => {
      return {
        id: item.newsId,
        title: item.title,
        content: item.content,
        publishedAt: item.newsDate ? new Date(item.newsDate) : null,
        expiresAt: item.newsDateExpire ? new Date(item.newsDateExpire) : null,
        newsLabel: item.newsLabel
      };
    })
  };
}

export async function fetchNewsDetails(newsId: number): Promise<INewsItem> {
  const { data } = await api.get(`/news/GetNewsDetails/${newsId}`);

  return {
    ...data,
    id: data.newsId,
    content: data.content,
    publishedAt: data.newsDate ? new Date(data.newsDate) : null,
    expiresAt: data.newsDateExpire ? new Date(data.newsDateExpire) : null,
    newsLabel: data.newsLabel
  };
}

export async function addNews(
  values: IAdminNewsFormSubmitValues
): Promise<INewsItem> {
  const { data } = await api.post(`/news/AddNews`, {
    ...values,
    newsLabel: values.newsLabel
  });
  return {
    ...data,
    id: data.newsId,
    content: data.content,
    publishedAt: data.newsDate ? new Date(data.newsDate) : null,
    expiresAt: data.newsDateExpire ? new Date(data.newsDateExpire) : null
  };
}

export async function editNews(
  newsId: number,
  values: object
): Promise<INewsItem> {
  const { data } = await api.post(`/news/UpdateNewsDetails`, {
    ...values,
    newsid: newsId
  });

  return {
    ...data,
    id: data.newsId,
    content: data.content,
    publishedAt: data.newsDate ? new Date(data.newsDate) : null,
    expiresAt: data.newsDateExpire ? new Date(data.newsDateExpire) : null,
    newsLabel: data.newsLabel
  };
}

export async function deleteNews(newsId: number) {
  await api.delete(`/news/DeleteNews/${newsId}`);
}

export async function fetchPages(): Promise<IPage[]> {
  const { data } = await api.get('/portalpage/GetPages');
  return data;
}

export async function fetchPageById(id: number): Promise<IPage> {
  const { data } = await api.get(`/portalpage/GetSpecificPageById/${id}`);
  return data;
}

export async function fetchPageBySlug(slug: string): Promise<IPage> {
  const { data } = await api.get(`/portalpage/GetPageBySlug/${slug}`);
  return data;
}

export async function addPage(page: INewPage): Promise<IPage> {
  const slug = '' + (Date.now() + Math.round(Math.random() * 1000));
  const { data } = await api.post('portalpage/AddPages', {
    ...page,
    slug
  });
  return data;
}

export async function addImageToPage(
  pageId: IPage['portalPageId'],
  image: Blob
): Promise<string[]> {
  const form = new FormData();
  form.append('image', image);
  form.append('portalpageid', pageId.toString());
  const { data } = await api.post('portalPage/AddImagesToPage', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
}

export interface ISearchCoursesResponse {
  page: number;
  totalPages: number;
  results: ICourseSearchResultItem[];
}

export async function searchCourses(
  query: ISearchBarState,
  config?: AxiosRequestConfig
): Promise<ISearchCoursesResponse> {
  const options = {
    keyword: query.searchQuery,
    themeId: query.theme ? query.theme : undefined,
    showRelevantCoursesOnly: query.searchScope === 'relevant' ? 1 : undefined
  };
  const { data } = await api.post(
    '/themes/GetCoursesInThemesSearch',
    options,
    config
  );

  data.results = data.listOfCourseSearchResult.map(
    (item: ICourseSearchResultItem) => {
      if (item.courseDescription) {
        item.courseDescription = item.courseDescription.replace(
          /<[^>]*>?/gm,
          ''
        );
      }

      return item;
    }
  );
  delete data.listOfCourseSearchResult;
  return data;
}

export interface ISearchNewsResponse {
  page: number;
  totalPages: number;
  results: INewsSearchResultItem[];
}

export async function searchNews(
  query: ISearchBarState,
  config?: AxiosRequestConfig
): Promise<ISearchNewsResponse> {
  const options = { keyword: query.searchQuery };
  const { data } = await api.post('/news/GetNewsSearch', options, config);

  data.results = data.newsSearchResult.map((item: INewsSearchResultItem) => {
    if (item.content) {
      item.content = item.content.replace(/<[^>]*>?/gm, '');
    }

    return item;
  });

  delete data.newsSearchResult;
  return data;
}

export async function updatePage(page: IPage) {
  await api.post('portalpage/UpdatePageDetails', page);
}

export async function reorderPages(pageList: IPage[]) {
  await api.post('portalpage/ReorderPages', pageList);
}

export async function deletePage(id: number) {
  await api.delete(`portalpage/DeletePage/${id}`);
}

export async function deleteImageFromPage(imageId: number) {
  await api.delete(`portalpage/DeleteImageFromPage/${imageId}`);
}

export async function getThemes(): Promise<ITheme[]> {
  const { data } = await api.get(`themes/GetThemes`);
  return data;
}

export async function updateTheme(
  themeId: number,
  theme: IThemeSectionFormValues
): Promise<ITheme> {
  const form = new FormData();

  if (theme.image != null) {
    form.append('image', theme.image);
  }

  form.append('themeId', themeId.toString());
  form.append('name', theme.name);
  form.append('description', theme.description);
  form.append('buttonText', theme.buttonText);
  const { data } = await api.post('themes/UpdateTheme', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
}


async function getStatisticReportTicket(): Promise<string> {
  const { data } = await api.get<string>('CourseportalStatistic/GetStatisticReportTicket');
  return data;
}

export async function getDepartments(): Promise<IDepartment[]> {
  const { data } = await api.get(`courseportalStatistic/GetDepartments`);
  return data;
}

export async function getStatisticResult(
  options: IGetStatisticResultOption
): Promise<IStatisticResult>{
  const ticket = await getStatisticReportTicket();
  const { sortOrder, ...body } = options;
  const params = { sortOrder, t: ticket };
  const { data } = await api.post(
    'CourseportalStatistic/GetStatisticResult',
    body,
    { params }
  );

  return data;
}

export async function getStatisticDetails(courseCode: number | string): Promise<IStatisticsDepartmentItem[]> {
  const ticket = await getStatisticReportTicket();
  const { data } = await api.get<IStatisticsDepartmentItem[]>(
    'CourseportalStatistic/GetStatisticResultCourseDetails',
    { params: { courseCode, t: ticket } },
  );

  return data;
}

export async function getEmailLists(): Promise<EmailList[]> {
  const { data } = await api.get('/courseportalemaillists');
  return data;
}

export async function deleteEmailList(id: string): Promise<void> {
  await api.delete(`/courseportalemaillists/${id}`);
}

export async function createEmailList(emailList: EmailList): Promise<EmailList> {
  const { data } = await api.post('/courseportalemaillists', {
    name: emailList.name,
    coursePortalEmails: emailList.coursePortalEmails,
  });
  return data;
}

export async function updateEmailList(emailList: EmailList): Promise<EmailList> {
  const { data } = await api.put(`/courseportalemaillists/${emailList.id}`, {
    name: emailList.name,
    coursePortalEmails: emailList.coursePortalEmails,
  });
  return data;
}

export async function queueStatisticReport(options: IQueueStatisticReportOptions): Promise<void> {
  const ticket = await getStatisticReportTicket();
  await api.post(
    'CourseportalStatistic/QueueNewReport',
    {
      ...options,
      filetype:'E',
      email: options.emails.join(',')
    },
    { params: { t: ticket } },
  );
};

const mapCourse = (course: IRawCourse): ICourse => {
  const [startDate, startTime] = course.startDateTime ? course.startDateTime.split('T') : [null, null];
  const [endDate, endTime] = course.endDateTime ? course.endDateTime.split('T') : [null, null];

  return {
    id: course.detailKursusId,
    name: course.activityName,
    categoryId: course.categoryId,
    categoryName: course.categoryName,
    themeId: course.themeId,
    locationName: course.locationName,
    publicationDate: course.publicationDate,
    startDate,
    startTime,
    endDate,
    endTime,
    lastEnrollmentDateTime: course.lastEnrollmentDateTime,
  };
};


export async function getCoursesGroupedByName(): Promise<ITitleItem[]> {
  const { data } = await api.get<Array<{
    minDetailKursusId: number;
    activityName: string;
    numberOfCourses: number;
  }>>('activity/GetCoursesGroupedByName');

  return data.map(({ minDetailKursusId, activityName, numberOfCourses }) => ({
    id: minDetailKursusId,
    title: activityName,
    numberOfCourses,
  }));
};

export const getCoursesGroupedByCategory = async (): Promise<ICoursesGroupedByCategory[]> => {
  const { data } = await api.get<
    Array<{
      categoryId: number;
      categoryName: string;
      coursesGroupedByName: Array<{
        minDetailKursusId: number;
        activityName: string;
        numberOfCourses: number;
      }>
    }>
  >('activity/GetCoursesGroupedByCategory');

  return data.map((category) => {
    return {
      categoryId: category.categoryId,
      name: category.categoryName,
      coursesGroupedByName: category.coursesGroupedByName.map(({ minDetailKursusId, activityName, numberOfCourses }) => ({
        id: minDetailKursusId,
        title: activityName,
        numberOfCourses,
      })),
    };
  })
};

export const getCoursesFromCategoryAndName = async (
  categoryId: string | number,
  id: string | number
): Promise<ICourse[]> => {
  const { data } = await api.get<IRawCourse[]>(`activity/GetCoursesFromCategoryAndName/${categoryId}/${id}`);
  return data.map(mapCourse);
};

export const getCoursesFromName = async (id: string | number): Promise<ICourse[]> => {
  const { data } = await api.get<IRawCourse[]>(`activity/GetCoursesFromName/${id}`);
  return data.map(mapCourse);
};

export type GetReportParams = {
  fromDate: string;
  toDate: string;
  organizers: number[];
};

export type ReportResult = {
  downloadUrl: string;
}

export const getFiuNumbersAndParticipants3FCoursePortalReport = async (
  params: GetReportParams
): Promise<ReportResult> => {
  const ticket = await getStatisticReportTicket();
  const url = `${process.env.REACT_APP_REPORT_API_URL}/FiuNumbersAndParticipants3FCoursePortalReport/generate-pdf`;
  const { data } = await api.post<ReportResult>(url, params, { params: { t: ticket } });
  return data;
};

export const getParticipantsCompleted3FCoursePortalReport = async (
  params: GetReportParams
): Promise<ReportResult> => {
  const ticket = await getStatisticReportTicket();
  const url = `${process.env.REACT_APP_REPORT_API_URL}/ParticipantsCompleted3FCoursePortalReport/generate-pdf`;
  const { data } = await api.post<ReportResult>(url, params, { params: { t: ticket } });
  return data;
}

export default api;



// WEBPACK FOOTER //
// ./src/helpers/api.ts